import { useEffect, useRef, useState } from 'preact/hooks';
import { Link } from 'wouter-preact';
import { api } from '@/lib/teaching/teacher-api';
import { assessments, refreshAssessments, yearFilter } from '@/lib/teaching/state';
import { errText } from '@/lib/teaching/slice2';
import { BUCKETS, BUCKET_LABEL, dateOf, type ClassProgress, type Distribution, type StrandRate, type TestInfo } from '@/lib/teaching/slice4';
import { TeachingPage, Card, YearSwitcher, BUCKET_FILL, INK, perfFill, pct, Plain } from '@/components/teaching/ui';
import { KpiRow, KpiCard } from '@/components/teaching/kit';
import { ErrorLine } from '@/components/teaching/form';
import { selectCls } from '@/components/teaching/setup';
import { strandMeaning } from '@/lib/teaching/strands';

// Class and cohort progress (slice 4 §04, U6). Like with like only: one
// criterion per line of tests, confirmed levels in buckets that sum to the
// population, strand rates with n. Raw % never appears as growth.

export function StackedBar({ d, compact }: { d: Distribution; compact?: boolean }) {
  const total = d.population || 1;
  return (
    <div>
      <div class={'flex w-full rounded overflow-hidden border border-[var(--color-border)] ' + (compact ? 'h-5' : 'h-7')} role="img"
        aria-label={BUCKETS.map((b) => `${BUCKET_LABEL[b]}: ${d.buckets[b]}`).join(', ')}>
        {BUCKETS.filter((b) => d.buckets[b] > 0).map((b) => (
          <div key={b} title={`${BUCKET_LABEL[b]}: ${d.buckets[b]}`}
            class={'h-full flex items-center justify-center text-[10.5px] font-medium tabular-nums overflow-hidden ' + (b === 'not_yet_confirmed' ? 'border-l border-dashed border-[#9aa0a8]' : '')}
            style={{ width: `${(d.buckets[b] / total) * 100}%`, background: BUCKET_FILL[b], color: INK }}>
            {d.buckets[b] / total >= 0.07 ? d.buckets[b] : ''}
          </div>
        ))}
      </div>
      {!compact && <div class="text-[11px] text-[var(--color-text-faint)] mt-1">{d.population} student{d.population === 1 ? '' : 's'} counted</div>}
    </div>
  );
}

export function BucketLegend() {
  return (
    <div class="flex flex-wrap items-center gap-1.5 text-[11px]">
      {BUCKETS.map((b) => (
        <span key={b} class={'px-1.5 py-0.5 rounded ' + (b === 'not_yet_confirmed' ? 'border border-dashed border-[#9aa0a8]' : '')} style={{ background: BUCKET_FILL[b], color: INK }}>{BUCKET_LABEL[b]}</span>
      ))}
    </div>
  );
}

function StrandList({ strands }: { strands: StrandRate[] }) {
  if (!strands.length) return <div class="text-[12px] text-[var(--color-text-faint)]">No marked evidence yet.</div>;
  return (
    <div class="flex flex-wrap gap-1.5">
      {strands.map((s) => (
        <span key={s.label} class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11.5px] tabular-nums" style={{ background: perfFill(s.rate), color: INK }}
          title={`Strand ${s.label}${strandMeaning(s.label) ? `: ${strandMeaning(s.label)}` : ''}. ${s.mastered} of ${s.n} answers mastered, ${s.items} questions, ${s.students} students`}>
          <b>Strand {s.label}</b>{strandMeaning(s.label) && <span class="hidden sm:inline">{strandMeaning(s.label)}</span>} {pct(s.rate)}
        </span>
      ))}
    </div>
  );
}

export function TestTag({ t }: { t: TestInfo }) {
  if (t.finalised) return <span class="text-[10.5px] px-1.5 py-0.5 rounded font-medium" style={{ background: '#d3f0d9', color: INK }}>Finalised</span>;
  if (t.provisional) return <span class="text-[10.5px] px-1.5 py-0.5 rounded font-medium border border-dashed border-[#c9a23a]" style={{ background: '#fff6e0', color: INK }}>Not final yet</span>;
  return <span class="text-[10.5px] px-1.5 py-0.5 rounded font-medium" style={{ background: '#e8effb', color: INK }}>Checkpoint 2 passed</span>;
}

export function Progress() {
  const [year, setYear] = useState<number | null>(yearFilter());
  const [cls, setCls] = useState('');
  const [criterion, setCriterion] = useState('');
  const [same, setSame] = useState(true);
  const [v, setV] = useState<ClassProgress | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const autoPicked = useRef(false);
  const req = useRef(0);
  useEffect(() => { if (!assessments.value) void refreshAssessments(); }, []);
  // Like with like: with several criteria on screen, open on the first one.
  useEffect(() => {
    if (v && !criterion && v.filters.year === year && v.criteria.length > 1 && !autoPicked.current) { autoPicked.current = true; setCriterion(v.criteria[0]); }
  }, [v]);

  useEffect(() => {
    const p = new URLSearchParams();
    if (year !== null) p.set('year', String(year));
    if (cls) p.set('class', cls);
    if (criterion) p.set('criterion', criterion);
    p.set('sameCohort', same ? 'true' : 'false');
    setErr(null);
    const mine = ++req.current;
    api<ClassProgress>('GET', `/api/t/progress?${p}`)
      .then((x) => { if (mine === req.current) setV(x); })
      .catch((e) => { if (mine === req.current) setErr(errText(e)); });
  }, [year, cls, criterion, same]);

  const criteria = v?.criteria ?? [];
  const multiCriteria = !criterion && criteria.length > 1;

  return (
    <TeachingPage title="Progress" breadcrumb={`${year === null ? 'All years' : `Year ${year}`}${criterion ? ` · Criterion ${criterion}` : ''} · MYP levels you confirmed and strand mastery, compared only like with like`}
      actions={<>
        <YearSwitcher onChange={(y) => { autoPicked.current = false; setYear(y); setCls(''); setCriterion(''); }} />
        <select aria-label="Class" class={selectCls} value={cls} onChange={(e) => setCls((e.target as HTMLSelectElement).value)}>
          <option value="">All classes</option>
          {v?.classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select aria-label="Criterion" class={selectCls} value={criterion} onChange={(e) => setCriterion((e.target as HTMLSelectElement).value)}>
          <option value="">All criteria</option>
          {criteria.map((c) => <option key={c} value={c}>Criterion {c}</option>)}
        </select>
        <label title="Count only students who sat every test shown, so a change in who sat a test cannot look like progress." class="inline-flex items-center gap-1.5 text-[12.5px] text-[var(--color-text)] h-9 px-2 rounded-xl border border-[var(--color-border)] bg-[var(--color-card)]">
          <input type="checkbox" checked={same} onChange={(e) => setSame((e.target as HTMLInputElement).checked)} />
          Only students in every test{v ? ` (${v.cohort.students})` : ''}
        </label>
      </>}>
      <ErrorLine error={err} />
      {!v && !err && <div class="text-[13px] text-[var(--color-text-muted)]">Loading…</div>}
      {v && (
        <>
          <KpiRow>
            <KpiCard value={v.tests.length} label="Tests shown" />
            <KpiCard value={same ? v.cohort.students : 'All'} label="Students counted" hint={same ? 'sat every test shown' : 'everyone present in each test'} />
            <KpiCard value={v.classes.length} label="Classes" />
            <KpiCard value={v.movers.length} label="Students to watch" hint="level fell or a mistake keeps coming back" />
          </KpiRow>
          <div class="text-[12.5px] text-[var(--color-text-muted)]">
            {same ? `Counting only the ${v.cohort.students} students who sat every test shown, so a change in who sat a test cannot look like progress or decline.` : 'Counting every student who was present for each test.'}
            {same && v.cohort.students === 0 && v.tests.length > 0 && <b class="text-[var(--color-text)]"> No student sat every test shown. Pick fewer tests, or untick "Only students in every test".</b>}
            {multiCriteria && ' Tests on different criteria are shown side by side and are not compared with each other.'}
          </div>
          <BucketLegend />
          {v.tests.length === 0 && <Card><div class="text-[13px] text-[var(--color-text-muted)]">No tests match these filters. Try All years or All classes.</div></Card>}
          <div class="grid md:grid-cols-2 xl:grid-cols-3 gap-3" data-progress-tests>
            {v.tests.map((t) => (
              <div key={t.test.id} data-test-card={t.test.title}
                class={'rounded-2xl bg-[var(--color-card)] p-4 flex flex-col gap-3 min-w-0 ' + (t.test.provisional ? 'border-2 border-dashed border-[#d9b65a]' : 'border border-[var(--color-border)]')}>
                <div class="flex items-start gap-2">
                  <div class="min-w-0">
                    <div class="text-[14px] font-semibold text-[var(--color-text)] truncate">{t.test.title}</div>
                    <div class="text-[11.5px] text-[var(--color-text-faint)]">Year {t.test.year_level} · Criterion {t.test.criterion} · {dateOf(t.test.sat_on)}</div>
                  </div>
                  <div class="ml-auto shrink-0"><TestTag t={t.test} /></div>
                </div>
                <div class="text-[12px] text-[var(--color-text-muted)] tabular-nums">
                  {t.counts.assessed} assessed · {t.counts.absent} absent · {t.counts.catchup} catch-up pending{t.counts.exempt ? ` · ${t.counts.exempt} exempt` : ''}
                </div>
                <div>
                  <div class="text-[10.5px] uppercase tracking-wider text-[var(--color-text-faint)] mb-1">MYP levels you confirmed</div>
                  <StackedBar d={t} />
                </div>
                <div>
                  <div class="text-[10.5px] uppercase tracking-wider text-[var(--color-text-faint)] mb-1">Strand mastery</div>
                  <StrandList strands={t.strands} />
                </div>
                <Link href={`/teaching/checkpoints?a=${t.test.id}`} class="text-[12px] text-[var(--color-accent)] hover:underline mt-auto">Checkpoints</Link>
              </div>
            ))}
          </div>

          {v.tests.some((t) => t.classes.length > 1) && (
            <Card title="Cohort comparison" sub="The same test, class by class">
              <div class="flex flex-col gap-4">
                {v.tests.filter((t) => t.classes.length > 1).map((t) => (
                  <div key={t.test.id}>
                    <div class="text-[13px] font-medium text-[var(--color-text)] mb-1.5">{t.test.title}</div>
                    <div class="overflow-x-auto">
                      <table class="w-full border-collapse text-[12px] sm:min-w-[560px]">
                        <thead><tr class="text-left text-[var(--color-text-muted)]">
                          <th class="sticky left-0 bg-[var(--color-card)] py-1 pr-3 font-medium w-[70px]">Class</th>
                          <th class="py-1 pr-3 font-medium w-[45%]">Levels</th>
                          <th class="py-1 font-medium hidden sm:table-cell">Strands</th>
                        </tr></thead>
                        <tbody>
                          {t.classes.map((c) => (
                            <tr key={c.class_name} class="border-t border-[var(--color-border)] align-top">
                              <td class="sticky left-0 bg-[var(--color-card)] py-1.5 pr-3 font-medium text-[var(--color-text)]"><span title={`${c.population} students counted`}>{c.class_name}</span></td>
                              <td class="py-1.5 pr-3"><StackedBar d={c} compact /><div class="sm:hidden mt-1.5"><StrandList strands={c.strands} /></div></td>
                              <td class="py-1.5 hidden sm:table-cell"><StrandList strands={c.strands} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          <Card title="Students to watch" sub="A confirmed level fell between comparable tests, or the same mistake came back">
            {v.movers.length === 0 ? <div class="text-[13px] text-[var(--color-text-muted)]">Nobody to watch on these filters: no level fell and no mistake came back.</div> : (
              <div class="flex flex-col">
                {v.movers.map((m, i) => (
                  <div key={i} class="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 py-1.5 border-b border-[var(--color-border)] last:border-0 text-[13px]">
                    <Link href={`/teaching/student/${m.student.id}/progress`} class="text-[var(--color-accent)] hover:underline font-medium">{m.student.name}</Link>
                    <span class="text-[10.5px] px-1.5 py-0.5 rounded" style={{ background: m.kind === 'level_fell' ? '#fddcbf' : '#fde9b8', color: INK }}>{m.kind === 'level_fell' ? 'Level fell' : 'Mistake came back'}</span>
                    <span class="text-[var(--color-text-muted)] min-w-0"><Plain text={m.reason} /></span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </>
      )}
      <div class="h-6" />
    </TeachingPage>
  );
}
